import { useState, useEffect } from "react";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import PrintPage from "./PrintPage";
import { useTranslation } from "react-i18next"; // ✅ i18n import

// Önceki sayfalarda kullandığımız eşleştirme sözlükleri
const paymentMapping = {
  nagt: "nagt",
  наличными: "nagt",
  cash: "nagt",
  price: "nagt",
  kart: "kart",
  карточкой: "kart",
  карта: "kart",
  card: "kart",
  terminal: "kart",
};

const statusMapping = {
  Garaşylýar: "garasylyar",
  "В ожидании": "garasylyar",
  "Kabul edilen": "kabul_edilen",
  Принято: "kabul_edilen",
  "Iberildi ýolda": "iberildi_yolda",
  "В пути": "iberildi_yolda",
  Tamamlandy: "tamamlandy",
  Завершено: "tamamlandy",
  "Ret edilen": "ret_edilen",
  Отклонено: "ret_edilen",
};

export default function AdminOrderDetails() {
  const { t, i18n } = useTranslation(); // ✅ Çeviri hook'u
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${CONSTANTS.baseRoute}/orders/${id}`);
        setOrder(response.data);
      } catch (err) {
        console.error("Error:", err);
        setError(t("order_load_error"));
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchOrderDetails();
  }, [id, t]);

  const handlePrint = () => {
    if (!order) return;
    setShowPrint(true);
    setTimeout(() => {
      window.print();
      setShowPrint(false);
    }, 100);
  };

  if (loading) return <div className="p-4">{t("yuklenyar")}...</div>;
  if (error) return <div className="text-danger p-4">{error}</div>;
  if (!order) return <div className="p-4">{t("sargyt_tapylmady")}</div>;

  const orderDetails = order;
  const orderItems = order.items;

  // Statü Butonları (Çevirili)
  const statuses = [
    { key: "Garaşylýar", label: t("garasylyar") },
    { key: "Kabul edilen", label: t("kabul_edilen") },
    { key: "Iberildi ýolda", label: t("iberildi_yolda") },
    { key: "Tamamlandy", label: t("tamamlandy") },
    { key: "Ret edilen", label: t("ret_edilen") },
  ];

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${CONSTANTS.baseRoute}/orders/${id}/status`, {
        status: newStatus,
      });
      setOrder({ ...order, status: newStatus });
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Garaşylýar: "text-warning",
      "Kabul edilen": "text-info",
      "Iberildi ýolda": "text-primary",
      Tamamlandy: "text-success",
      "Ret edilen": "text-danger",
    };
    return colors[status] || "";
  };

  const getPaymentLabel = (method) => {
    const key = paymentMapping[method?.toLowerCase()] || method;
    return t(key);
  };

  return (
    <div className="admin-main py-2 px-3">
      <div className="d-flex gap-2 align-items-center mb-2">
        <button className="btn btn-light" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn btn-light" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold">
          {t("sargyt")} № <span>{orderDetails.id}</span>
        </h5>
      </div>

      <div className="non-printable">
        <div className="d-flex flex-wrap justify-content-between mb-2 gap-2">
          <div className="d-flex flex-wrap gap-2">
            {statuses.map((st) => {
              const isActive = orderDetails.status === st.key;
              return (
                <button
                  key={st.key}
                  className={`rounded-1 px-3 py-1 small ${
                    isActive ? "admin-hover-active" : "admin-hover"
                  }`}
                  onClick={() => !isActive && handleStatusChange(st.key)}
                  disabled={isActive}
                >
                  {st.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={handlePrint}
            className="btn d-flex gap-2 align-items-center bg-light border small"
          >
            <i className="bi bi-printer"></i>
            <span>{t("cap_etmek")}</span>
          </button>
        </div>

        <div className="row g-3 d-flex">
          {/* Ürün Tablosu */}
          <div className="table-responsive card p-2 shadow-sm col-md-8">
            <table className="table align-middle table-hover text-center small">
              <thead>
                <tr>
                  <th style={{ color: "#a9a9a9" }}>№</th>
                  <th style={{ color: "#a9a9a9" }}>{t("surat")}</th>
                  <th
                    className="text-start"
                    style={{ width: "25%", color: "#a9a9a9" }}
                  >
                    {t("ady")}
                  </th>
                  <th style={{ color: "#a9a9a9" }}>{t("barkod")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("sany")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("bahasy")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("arzanladys")}</th>
                  <th style={{ color: "#a9a9a9" }}>{t("jemi")}</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => {
                  const originalPrice = Number(item.price || 0);

                  // 🔥 0 veya 0% veya null ise indirim yok say
                  const discountValue = Number(item.discount);
                  const hasDiscount =
                    item.discount !== null &&
                    item.discount !== undefined &&
                    discountValue > 0;

                  const discountedPrice = hasDiscount
                    ? discountValue
                    : originalPrice;

                  const lineTotal = (item.quantity * discountedPrice).toFixed(
                    2,
                  );
                  return (
                    <tr key={index}>
                      <td className="fw-bold text-danger">{index + 1}</td>
                      <td>
                        <img
                          src={
                            item.image
                              ? `${CONSTANTS.baseRoute}${item.image}`
                              : "/logo.png"
                          }
                          alt=""
                          width={60}
                          height={60}
                          className={`object-fit-contain border rounded ${
                            !item.image ? "p-2" : ""
                          }`}
                        />
                      </td>

                      <td className="text-start">
                        {i18n.language === "ru"
                          ? item.product_name_ru
                          : item.product_name_tm}
                      </td>
                      <td>{item.product_barcode}</td>
                      <td>x {item.quantity}</td>
                      <td>{originalPrice.toFixed(2)} m</td>
                      <td>
                        {hasDiscount ? `${discountedPrice.toFixed(2)} m` : "-"}
                      </td>
                      <td className="fw-bold">{lineTotal} m</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sipariş Detay Kartı */}
          <div className="col-md-4">
            <div className="p-3 shadow-sm card small">
              {[
                { label: t("ulanyjynyn_ady"), value: orderDetails.full_name },
                {
                  label: t("doredilen_sene"),
                  value: new Date(orderDetails.created_at).toLocaleString(),
                },
                { label: t("eltmeli_wagty"), value: orderDetails.time },
                { label: t("sargyt_nomeri"), value: orderDetails.id },
                {
                  label: t("yagdayy"),
                  value: t(
                    statusMapping[orderDetails.status] || orderDetails.status,
                  ),
                  className: getStatusColor(orderDetails.status),
                },
                {
                  label: t("telefon_belgisi"),
                  value: orderDetails.phone_number,
                },
                {
                  label: t("salgysy"),
                  value: `${orderDetails.region}, ${orderDetails.city || ""}, ${orderDetails.address_details || ""}`,
                },
                { label: t("bellik"), value: orderDetails.notes || t("yok") },
                {
                  label: t("toleg_gornusi"),
                  value: getPaymentLabel(orderDetails.payment_method),
                },
              ].map((item, idx) => (
                <div key={idx}>
                  <div className="d-flex justify-content-between py-2 fw-semibold">
                    <span className="text-muted">{item.label}:</span>
                    <span className={item.className || ""}>
                      {item.value || "N/A"}
                    </span>
                  </div>
                  {idx !== 8 && <hr className="my-0 opacity-25" />}
                </div>
              ))}

              <div className="mt-3 p-2 bg-light rounded">
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>{t("eltip_bermek")}:</span>
                  <span>
                    {Number(orderDetails.delivery_price || 0).toFixed(2)} m
                  </span>
                </div>
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>{t("subtotal")}:</span>
                  <span>
                    {(
                      Number(orderDetails.total_price) -
                      Number(orderDetails.delivery_price || 0)
                    ).toFixed(2)}{" "}
                    m
                  </span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fs-5 fw-bold text-primary">
                  <span>{t("jemi_bahasy")}:</span>
                  <span>
                    {Number(orderDetails.total_price || 0).toFixed(2)} m
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPrint && <PrintPage order={order} />}
    </div>
  );
}
